import * as React from 'react'
import debounce from 'lodash/debounce'

import { usePersonalisationToggleContext } from '@thg-commerce/enterprise-components/Personalisation'
import {
  EnterpriseContext,
  i18n,
  LiveMessage,
  useRequestConfig,
  useSiteConfig,
  useSiteDefinition,
  useTransmit,
} from '@thg-commerce/enterprise-core'
import { getColumboEventData } from '@thg-commerce/enterprise-metrics'
import { PersonalisationGallery } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/Product'
import {
  styled,
  useTheme,
} from '@thg-commerce/enterprise-pages/src/Product/theme'
import { AriaMessageType } from '@thg-commerce/gravity-elements'
import { ImageGallery } from '@thg-commerce/gravity-patterns'
import ShareButton from '@thg-commerce/gravity-patterns/ImageGallery/inAppShareButton'
import { Position } from '@thg-commerce/gravity-patterns/ImageGallery/types'
import {
  BreakpointArray,
  Direction,
  mediaQueryRenderer,
  mq,
  spacing,
} from '@thg-commerce/gravity-theme'

import { useProductPage } from '../../context/ProductPageContext'
import { ProductImageGalleryZoomPresenter } from '../ProductImageGalleryZoomPresenter'

interface ImageGalleryRendererProps {
  className?: string
}

const StyledImageGallery = styled(ImageGallery)`
  ${(props) =>
    props.theme?.pageTheme?.imageGallery.carousel.gutterMargin
      ? mediaQueryRenderer(
          props.theme.pageTheme.imageGallery.carousel.gutterMargin,
          (margin) => `margin: 0 ${spacing(margin)};`,
        )
      : ''}

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    margin-bottom: ${spacing(4)};
  }
`

const getImageClassName = (hasShoeSizeMeId) => {
  return !!hasShoeSizeMeId ? 'athenaProductImageCarousel_image' : ''
}

export const ImageGalleryRenderer: React.FunctionComponent<ImageGalleryRendererProps> = (
  props,
) => {
  const {
    product,
    selectedVariant,
    presentImageGalleryZoomModal,
  } = useProductPage()
  const {
    enablePDPGalleryZoom,
    enablePDPGalleryZoomDesktop,
    enableProductPersonalisation,
    enablePDPGalleryOverflow,
    enableInAppShare,
    hasShoeSizeMeId,
  } = useSiteConfig()
  const {
    icons: { zoomIn },
  } = useTheme()

  const { isMobileApp } = useRequestConfig()

  const theme = useTheme()
  const transmit = useTransmit()
  const EnterpriseCtx = React.useContext(EnterpriseContext)

  const {
    personalisationImagesContext: personalisationImages,
  } = usePersonalisationToggleContext()

  const { defaultLocale, subsite, siteId, channel } = useSiteDefinition()
  const propertyArgs = {
    defaultLocale,
    subsite,
    siteId,
    channel,
    customerLocation: EnterpriseCtx.currentLocation,
  }

  const i18nText = {
    carouselI18nText: {
      leftScrollLabel: i18n('carousel.controls.left.arialabel'),
      rightScrollLabel: i18n('carousel.controls.right.arialabel'),
    },
    thumbnailButtonAriaLabel: i18n('imagethumbnails.arialabel.text').concat(
      product?.title || '',
    ),
    fullscreenI18nText: {
      enterFullscreenText: i18n('imagegallery.fullscreen.enter.text'),
      exitFullscreenText: i18n('imagegallery.fullscreen.exit.text'),
    },
    zoomInLabel: i18n('imagegallery.controls.zoomin.arialabel'),
    zoomOutLabel: i18n('imagegallery.controls.zoomout.arialabel'),
    announcerI18nText: {
      imageChangeAnnouncerText: i18n('imagegallery.announcer.imagechange.text'),
      zoomLevelAnnouncerText: i18n('imagegallery.announcer.zoomlevel.text'),
      fullscreenEnterAnnouncerText: i18n(
        'imagegallery.announcer.fullscreenview.enter.text',
      ),
      fullscreenExitAnnouncerText: i18n(
        'imagegallery.announcer.fullscreenview.exit.text',
      ),
      panI18nText: {
        imagePanAnnouncerText: i18n('imagegallery.announcer.panning.text'),
        leftText: i18n('imagegallery.announcer.pan.direction.left.text'),
        rightText: i18n('imagegallery.announcer.pan.direction.right.text'),
        downText: i18n('imagegallery.announcer.pan.direction.down.text'),
        upText: i18n('imagegallery.announcer.pan.direction.up.text'),
      },
    },
    fullscreenIndexI18nText: i18n('imagegallery.fullscreen.index.text'),
    accessibilityLabel: i18n('imagegallery.accessibilitylabel.text'),
    closeI18nText: {
      closeAriaLabel: i18n('general.modal.close.button.arialabel'),
      closeLabel: i18n('general.modal.close.button.label'),
    },
  }

  const direction: BreakpointArray<Direction> = theme.pageTheme.imageGallery
    .carousel.direction || [
    Direction.COLUMN,
    Direction.COLUMN,
    Direction.ROW,
    Direction.ROW,
  ]

  const transmitEvent = (eventSubtype: string, eventType: string) => {
    transmitColumboEvent({
      eventType,
      eventSubtype,
      argumentsObj: {
        '0': 'Accessibility',
        '1': eventType === 'click' ? 'Click' : 'Swipe',
        '2': 'productImageCarousel',
      },
    })
  }

  const debouncedTransmitEvent = React.useCallback(
    debounce((eventSubtype: string, eventType: string) => {
      transmitColumboEvent({
        eventType,
        eventSubtype,
        argumentsObj: {
          '0': 'Accessibility',
          '1': eventType === 'click' ? 'Click' : 'Swipe',
          '2': 'productImageCarousel',
        },
      })
    }, 300),
    [],
  )

  const transmitColumboEvent = ({
    argumentsObj,
    eventSubtype,
    eventType,
  }: {
    argumentsObj: { [key: string]: string }
    eventType: string
    eventSubtype: string
  }) => {
    transmit({
      type: 'columbo',
      payload: getColumboEventData({
        propertyArgs,
        argumentsObj,
        requestArgs: {
          client_timestamp: Math.round(Date.now() / 1000),
          url: window.location.href,
        },
        eventData: {
          type: eventType,
          subtype: eventSubtype,
          contents: [],
        },
        nonce: EnterpriseCtx.metricNonce,
      }),
    })
  }

  const onNavClick = (direction: 'left' | 'right') => {
    const eventSubtype = `productImageCarousel_${
      direction === 'left' ? 'leftArrow' : 'rightArrow'
    }`
    transmitEvent(eventSubtype, 'click')
  }

  const onSwipe = () => debouncedTransmitEvent('productImageCarousel', 'swipe')

  const onThumbnailClick = () =>
    debouncedTransmitEvent('productImageCarousel_thumbnailWrapper', 'click')

  const imageUrls = () => {
    const personalisationImage =
      enableProductPersonalisation && personalisationImages

    const validPersonalisationImage =
      personalisationImage && personalisationImage.images.some((img) => img.url)

    const selectedGallery = validPersonalisationImage
      ? personalisationImage
      : selectedVariant?.gallery ?? product?.gallery

    return selectedGallery || { thumbnails: [], images: [], zoom: [] }
  }

  const shareButtonProps = React.useMemo(
    () => ({
      isMobileApp,
      enableInAppShare,
      sku: product?.sku,
      pdpLink: product?.url,
    }),
    [isMobileApp, enableInAppShare, product?.sku, product?.url],
  )

  return (
    <React.Fragment>
      <ShareButton {...shareButtonProps} />
      <StyledImageGallery
        onSwipe={onSwipe}
        onNavClick={onNavClick}
        onThumbnailClick={onThumbnailClick}
        zoomEnabled={enablePDPGalleryZoom}
        desktopZoomEnabled={enablePDPGalleryZoomDesktop}
        zoomOnClickHandler={(imageIndex) =>
          presentImageGalleryZoomModal?.current(imageIndex)
        }
        urls={imageUrls()}
        altText={product?.title || ''}
        i18nText={i18nText}
        ampProps={{
          height: 0,
          width: 0,
        }}
        renderAnnouncer={(ariaLive: AriaMessageType, message: string) => (
          <LiveMessage ariaLive={ariaLive} message={message} />
        )}
        controlIcons={{
          zoomIn: zoomIn?.svgPath,
          fill: theme.colors.palette.greys.dark,
        }}
        direction={direction}
        controlSize={
          theme.pageTheme?.imageGallery?.carousel?.controls?.size || [48]
        }
        controlBorderColor={
          theme.pageTheme?.imageGallery?.carousel?.controls?.border ||
          theme.colors.palette.brand.base
        }
        controlBackgroundColor={
          theme.pageTheme?.imageGallery.carousel?.controls?.background ||
          theme.colors.palette.greys.white
        }
        thumbnailSize={[78]}
        thumbnailsMarginX={
          theme.pageTheme.imageGallery.carousel.thumbnails?.marginXSpacing
        }
        zoomControlPosition={[
          Position.BOTTOM_RIGHT,
          Position.BOTTOM_RIGHT,
          Position.TOP_RIGHT,
        ]}
        fullscreenControlPosition={[Position.TOP_RIGHT]}
        showCarouselControls={[true]}
        showFooter={theme.pageTheme.imageGallery.carousel.showFooter}
        className={props.className}
        itemsPerSlide={theme.pageTheme.imageGallery.carousel.itemsPerSlide || 1}
        gapSpacing={theme.pageTheme.imageGallery.carousel.gapSpacing || [2]}
        carousel={{
          itemGapSpacing:
            theme.pageTheme.imageGallery.carousel.itemGapSpacing || 0,
          controls: {
            visible: theme.pageTheme.imageGallery.carousel.controls?.visible,
            placement:
              theme.pageTheme.imageGallery.carousel.controls?.placement,
            size: theme.pageTheme.imageGallery.carousel.controls?.size,
            controlBorderColour:
              theme.pageTheme?.imageGallery?.carousel?.controls
                ?.controlBorderColour,
            controlHoverBackgroundColor:
              theme.pageTheme?.imageGallery?.carousel?.controls
                ?.controlHoverBackgroundColor,
          },
          overflow: theme.pageTheme.imageGallery.carousel.overflow,
          onHydrateVisibleFetchPriority: 'high',
        }}
        zoomControl={true}
        updatingUrlsResetSelectedImageIndex={true}
        imageOverflow={enablePDPGalleryOverflow}
        imageClassName={getImageClassName(hasShoeSizeMeId)}
      />
      <ProductImageGalleryZoomPresenter />
    </React.Fragment>
  )
}
